/**
 * RoeBlocks — sections de formulaire du Relevé d'emploi (blocs 4-22), toutes modifiables. Batch #14.264.
 */
import { Plus, Trash2 } from "lucide-react";
import { Field, inputCls, Btn, num } from "../_shared";

const In = ({ k, f, set, type = "text", testId, ...rest }) => (
  <input type={type} className={inputCls} value={f[k] ?? ""} onChange={(e) => set(k, type === "number" ? e.target.value : e.target.value)} data-testid={testId || `paie-roe-${k.replace(/_/g, "-")}`} {...rest} />
);
const Sel = ({ k, f, set, options, testId, empty }) => (
  <select className={inputCls} value={f[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={testId || `paie-roe-${k.replace(/_/g, "-")}`}>
    {empty && <option value="">{empty}</option>}
    {Object.entries(options).map(([v, l]) => <option key={v} value={v}>{`${v} — ${l}`}</option>)}
  </select>
);

export function EmployerBlock({ f, set, codes }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      <Field label="4 Nom de l'employeur" className="sm:col-span-2"><In k="b4_employer_name" f={f} set={set} /></Field>
      <Field label="5 N° de compte de retenues ARC (RP)"><In k="b5_cra_account" f={f} set={set} /></Field>
      <Field label="4 Adresse" className="sm:col-span-2"><In k="b4_employer_address" f={f} set={set} /></Field>
      <Field label="6 Genre de période de paie"><Sel k="b6_pay_period_type" f={f} set={set} options={codes.pay_periods} /></Field>
      <Field label="Ville"><In k="b4_employer_city" f={f} set={set} /></Field>
      <Field label="Province"><In k="b4_employer_prov" f={f} set={set} maxLength={2} /></Field>
      <Field label="7 Code postal"><In k="b4_employer_postal" f={f} set={set} /></Field>
    </div>
  );
}

export function EmployeeBlock({ f, set }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      <Field label="8 Numéro d'assurance sociale" hint={f.b8_sin_ok ? "Depuis la fiche employé (masqué)" : "Manquant — complétez la fiche employé"}><input className={`${inputCls} ${f.b8_sin_ok ? "" : "border-rose-400"}`} value={f.b8_sin_masked || ""} readOnly data-testid="paie-roe-b8-sin" /></Field>
      <Field label="9 Prénom"><In k="b9_first_name" f={f} set={set} /></Field>
      <Field label="9 Nom"><In k="b9_last_name" f={f} set={set} /></Field>
      <Field label="9 Adresse de l'employé" className="sm:col-span-2"><In k="b9_address" f={f} set={set} /></Field>
      <Field label="13 Profession"><In k="b13_occupation" f={f} set={set} /></Field>
    </div>
  );
}

export function DatesBlock({ f, set, codes }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      <Field label="10 Premier jour de travail"><In k="b10_first_day" f={f} set={set} type="date" /></Field>
      <Field label="11 Dernier jour payé"><In k="b11_last_day_paid" f={f} set={set} type="date" /></Field>
      <Field label="12 Date de fin de la dernière période de paie"><In k="b12_final_period_end" f={f} set={set} type="date" /></Field>
      <Field label="14 Date prévue de rappel" className="sm:col-span-2"><Sel k="b14_recall_code" f={f} set={set} options={codes.recall} /></Field>
      {f.b14_recall_code === "Y" && <Field label="Date de rappel"><In k="b14_recall_date" f={f} set={set} type="date" /></Field>}
    </div>
  );
}

export function EarningsBlock({ f, set, computed }) {
  const pp = f.b15c || [];
  const upd = (i, k, v) => set("b15c", pp.map((p, j) => (j === i ? { ...p, [k]: v } : p)));
  const add = () => set("b15c", [...pp, { pp: pp.length + 1, amount: 0, hours: 0, period_start: "", period_end: "" }]);
  const del = (i) => set("b15c", pp.filter((_, j) => j !== i).map((p, j) => ({ ...p, pp: j + 1 })));
  const sum = (arr, k) => arr.reduce((s, p) => s + num(p[k]), 0);
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Field label="15A Total des heures assurables" hint={computed ? `Calculé : ${Number(computed.b15a_hours || 0).toFixed(2)} h (somme des périodes ci-dessous : ${sum(pp, "hours").toFixed(2)})` : ""}><In k="b15a_hours" f={f} set={set} type="number" step="0.01" /></Field>
        <Field label="15B Total de la rémunération assurable" hint={computed ? `Calculé : ${Number(computed.b15b_earnings || 0).toFixed(2)} $ (27 / 14 / 7 dernières périodes selon la fréquence)` : ""}><In k="b15b_earnings" f={f} set={set} type="number" step="0.01" /></Field>
        <div className="text-xs text-slate-500 self-end pb-2">{pp.length} période(s) au bloc 15C · total {sum(pp, "amount").toFixed(2)} $</div>
      </div>
      <div className="overflow-x-auto -mx-1">
        <table className="w-full text-xs" data-testid="paie-roe-b15c-table">
          <thead><tr className="text-[10px] uppercase text-slate-500"><th className="px-2 py-1 text-left">PP</th><th className="px-2 py-1 text-left">Début</th><th className="px-2 py-1 text-left">Fin</th><th className="px-2 py-1 text-right">Heures</th><th className="px-2 py-1 text-right">Rémunération assurable</th><th /></tr></thead>
          <tbody>
            {pp.map((p, i) => (
              <tr key={i} className="border-t border-slate-100" data-testid={`paie-roe-b15c-row-${i}`}>
                <td className="px-2 py-1 font-mono">{p.pp}</td>
                <td className="px-2 py-1"><input type="date" className="border rounded px-1 py-0.5" value={p.period_start || ""} onChange={(e) => upd(i, "period_start", e.target.value)} /></td>
                <td className="px-2 py-1"><input type="date" className="border rounded px-1 py-0.5" value={p.period_end || ""} onChange={(e) => upd(i, "period_end", e.target.value)} /></td>
                <td className="px-2 py-1 text-right"><input type="number" step="0.01" className="border rounded px-1 py-0.5 w-20 text-right font-mono" value={p.hours ?? ""} onChange={(e) => upd(i, "hours", e.target.value)} data-testid={`paie-roe-b15c-hours-${i}`} /></td>
                <td className="px-2 py-1 text-right"><input type="number" step="0.01" className="border rounded px-1 py-0.5 w-28 text-right font-mono" value={p.amount ?? ""} onChange={(e) => upd(i, "amount", e.target.value)} data-testid={`paie-roe-b15c-amount-${i}`} /></td>
                <td className="px-2 py-1 text-right"><button type="button" onClick={() => del(i)} className="p-1 text-rose-600 hover:bg-rose-50 rounded" aria-label="Retirer"><Trash2 className="w-3.5 h-3.5" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Btn tone="slate" icon={Plus} onClick={add} testId="paie-roe-b15c-add">Ajouter une période</Btn>
    </div>
  );
}

export function ReasonBlock({ f, set, codes }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      <Field label="16 Raison du présent relevé *" className="sm:col-span-3"><Sel k="b16_reason_code" f={f} set={set} options={codes.reasons} empty="— Choisir la raison —" /></Field>
      <Field label="16 Personne-ressource (nom)"><In k="b16_contact_name" f={f} set={set} /></Field>
      <Field label="16 Téléphone"><In k="b16_contact_phone" f={f} set={set} /></Field>
      <Field label="20 Langue de communication"><Sel k="b20_language" f={f} set={set} options={codes.languages} /></Field>
      <Field label="18 Observations" className="sm:col-span-3" hint="Obligatoire si la raison est K (autre)"><textarea className={`${inputCls} min-h-[60px]`} value={f.b18_comments || ""} onChange={(e) => set("b18_comments", e.target.value)} maxLength={160} data-testid="paie-roe-b18-comments" /></Field>
      <Field label="22 Nom de l'émetteur"><In k="b22_issuer_name" f={f} set={set} /></Field>
      <Field label="21 Téléphone de l'émetteur"><In k="b22_issuer_phone" f={f} set={set} /></Field>
    </div>
  );
}

function ListRows({ items, onChange, cols, testId, addLabel, blank }) {
  const upd = (i, k, v) => onChange(items.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <div className="space-y-1.5" data-testid={testId}>
      {items.map((it, i) => (
        <div key={i} className="flex flex-wrap items-center gap-1.5">
          {cols.map((c) => c.type === "select" ? (
            <select key={c.k} className="border rounded px-1.5 py-1 text-xs" value={it[c.k] || ""} onChange={(e) => upd(i, c.k, e.target.value)}>{Object.entries(c.options).map(([v, l]) => <option key={v} value={v}>{`${v} — ${l}`}</option>)}</select>
          ) : (
            <input key={c.k} type={c.type} step="0.01" placeholder={c.ph} className={`border rounded px-1.5 py-1 text-xs ${c.type === "number" ? "w-28 text-right font-mono" : ""}`} value={it[c.k] ?? ""} onChange={(e) => upd(i, c.k, e.target.value)} />
          ))}
          <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="p-1 text-rose-600 hover:bg-rose-50 rounded" aria-label="Retirer"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      ))}
      <Btn tone="slate" icon={Plus} onClick={() => onChange([...items, blank])} testId={`${testId}-add`}>{addLabel}</Btn>
    </div>
  );
}

export function SeparationBlock({ f, set, codes }) {
  const vac = f.b17a_vacation || {};
  const setVac = (k, v) => set("b17a_vacation", { ...vac, [k]: v });
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <Field label="17A Paie de vacances — montant"><input type="number" step="0.01" className={inputCls} value={vac.amount ?? ""} onChange={(e) => setVac("amount", e.target.value)} data-testid="paie-roe-b17a-amount" /></Field>
        <Field label="17A Code" className="sm:col-span-2"><select className={inputCls} value={vac.code || ""} onChange={(e) => setVac("code", e.target.value)} data-testid="paie-roe-b17a-code"><option value="">—</option>{Object.entries(codes.vacation).map(([v, l]) => <option key={v} value={v}>{`${v} — ${l}`}</option>)}</select></Field>
        {vac.code === "3" && <Field label="Période (début)"><input type="date" className={inputCls} value={vac.start || ""} onChange={(e) => setVac("start", e.target.value)} /></Field>}
      </div>
      <Field label="17B Congés fériés payés après le dernier jour payé">
        <ListRows items={f.b17b_holidays || []} onChange={(v) => set("b17b_holidays", v)} testId="paie-roe-b17b" addLabel="Ajouter un férié" blank={{ date: "", amount: "" }}
          cols={[{ k: "date", type: "date" }, { k: "amount", type: "number", ph: "Montant" }]} />
      </Field>
      <Field label="17C Autres sommes (indemnité de départ, préavis, primes…)">
        <ListRows items={f.b17c_other || []} onChange={(v) => set("b17c_other", v)} testId="paie-roe-b17c" addLabel="Ajouter une somme" blank={{ code: "E00", amount: "", start: "", end: "" }}
          cols={[{ k: "code", type: "select", options: codes.other_monies }, { k: "amount", type: "number", ph: "Montant" }, { k: "start", type: "date" }, { k: "end", type: "date" }]} />
      </Field>
    </div>
  );
}
